import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, Play, Square, LocateFixed, Package, PackageCheck, Navigation, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicDriver() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sharing, setSharing] = useState(false);
  const [lastSent, setLastSent] = useState(null);
  const posRef = useRef(null);
  const watchRef = useRef(null);
  const timerRef = useRef(null);

  const load = useCallback((quiet) => {
    axios.get(`${API}/p/driver/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => { if (!quiet) setErr(e.response?.data?.detail || "This driver link is not valid."); });
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => load(true), 15000);
    return () => clearInterval(id);
  }, [load]);

  const stop = useCallback(() => {
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    watchRef.current = null; timerRef.current = null;
    setSharing(false);
  }, []);
  useEffect(() => () => stop(), [stop]);

  async function sendPing() {
    if (!posRef.current) return;
    try {
      await axios.post(`${API}/p/driver/${token}/location`, {
        lat: posRef.current.lat, lng: posRef.current.lng, accuracy: posRef.current.accuracy,
      });
      setLastSent(new Date());
    } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("ping failed", e?.message || e); }
  }

  function startShare() {
    if (!navigator.geolocation) { setErr("Your phone does not support location."); return; }
    setErr(""); setSharing(true);
    let first = false;
    watchRef.current = navigator.geolocation.watchPosition(
      (p) => { posRef.current = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }; if (!first) { first = true; sendPing(); } },
      () => setErr("Could not get location. Please allow location access."),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 });
    timerRef.current = setInterval(sendPing, 8000);
  }

  async function setStatus(s, status) {
    try {
      await axios.post(`${API}/p/driver/${token}/stops/${s.id}/status`, { status });
      setData((d) => ({ ...d, stops: d.stops.map((x) => x.id === s.id ? { ...x, status } : x) }));
    } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("status update failed, will retry on next poll", e?.message || e); }
  }

  if (err && !data) return <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] text-[#8A2526] text-sm p-6" data-testid="driver-error">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] text-[#68665E] text-sm">Loading…</div>;

  const stops = data.stops || [];
  const doneCount = stops.filter((s) => s.status === "done").length;
  const allDone = stops.length > 0 && doneCount === stops.length;

  return (
    <div className="min-h-screen bg-[#FDFCF8] pb-24" data-testid="driver-page">
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-5">
          <img src="/ito-logo.png" alt="ITO" className="w-10 h-10 object-contain" />
          <div>
            <div className="font-headings font-bold text-[#1A1A18] text-sm leading-tight">INDIAN TRANSPORT ORGANISATION</div>
            <div className="text-[10px] uppercase tracking-widest text-[#68665E]">EV Tempo Dispatch · {data.driver_name}{data.vehicle_number ? ` · ${data.vehicle_number}` : ""}</div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E3DB] rounded-lg p-3 mb-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase text-[#68665E]">Today's route</div>
            <div className="font-headings text-2xl font-bold text-[#1A1A18]" data-testid="driver-progress">{doneCount}/{stops.length} done</div>
          </div>
          <Navigation className="w-8 h-8 text-[#C84B31]" />
        </div>

        {stops.length === 0 ? (
          <div className="text-sm text-[#68665E] border border-dashed border-[#E5E3DB] rounded p-8 text-center" data-testid="driver-empty">No stops assigned yet. Please check back shortly.</div>
        ) : (
          <div className="space-y-3" data-testid="driver-stops">
            {stops.map((s, i) => {
              const done = s.status === "done";
              const arrived = s.status === "arrived";
              const Icon = s.stop_type === "pickup" ? Package : PackageCheck;
              const mapUrl = s.lat != null ? `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}` : (s.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address)}` : "");
              return (
                <div key={s.id} className={`bg-white border rounded-lg p-3 ${done ? "border-[#2F6F4E] opacity-70" : "border-[#E5E3DB]"}`} data-testid={`driver-stop-${s.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${done ? "bg-[#2F6F4E]" : "bg-[#1A1A18]"} text-white`}>{done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[#1A1A18] flex items-center gap-1"><Icon className="w-4 h-4 text-[#C84B31]" />{s.stop_type === "pickup" ? "PICK UP" : "DELIVER"}{s.lr_number && <span className="font-mono text-[11px] text-[#2B5FB3] ml-1">{s.lr_number}</span>}</div>
                      <div className="text-sm text-[#1A1A18] mt-0.5">{s.company || "—"}</div>
                      <div className="text-[12px] text-[#68665E] flex items-start gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />{s.address || "—"}</div>
                      {s.contact_phone && <a href={`tel:${s.contact_phone}`} className="text-[12px] text-[#2B5FB3]">{s.contact_name} · {s.contact_phone}</a>}
                      {s.notes && <div className="text-[11px] text-[#68665E] italic mt-0.5">{s.notes}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    {mapUrl && <a href={mapUrl} target="_blank" rel="noreferrer" className="btn-secondary !py-1.5 text-xs flex-1 justify-center" data-testid={`driver-nav-${s.id}`}><Navigation className="w-4 h-4" /> Navigate</a>}
                    {!done ? (
                      <>
                        {!arrived && <button onClick={() => setStatus(s, "arrived")} className="btn-secondary !py-1.5 text-xs flex-1 justify-center" data-testid={`driver-arrived-${s.id}`}>Arrived</button>}
                        <button onClick={() => setStatus(s, "done")} className="btn-primary !py-1.5 text-xs flex-1 justify-center" data-testid={`driver-done-${s.id}`}>{s.stop_type === "pickup" ? "Picked" : "Delivered"}</button>
                      </>
                    ) : (
                      <button onClick={() => setStatus(s, "pending")} className="btn-ghost !py-1.5 text-xs" data-testid={`driver-undo-${s.id}`}>Undo</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {allDone && <div className="mt-4 text-center text-[#2F6F4E] text-sm font-medium" data-testid="driver-all-done"><CheckCircle2 className="w-6 h-6 mx-auto mb-1" /> All stops complete. Great work!</div>}
      </div>

      {/* Location sharing bar */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-[#E5E3DB] p-3">
        <div className="max-w-md mx-auto">
          {err && <div className="text-xs text-[#8A2526] mb-2 text-center" data-testid="driver-inline-error">{err}</div>}
          {!sharing ? (
            <button onClick={startShare} className="btn-primary w-full justify-center" data-testid="driver-share-start"><Play className="w-4 h-4" /> Start sharing my location</button>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-[#2F6F4E] text-sm font-medium" data-testid="driver-share-active">
                <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2F6F4E] opacity-60" /><span className="relative inline-flex rounded-full h-3 w-3 bg-[#2F6F4E]" /></span>
                <LocateFixed className="w-4 h-4" /> Sharing{lastSent ? ` · ${lastSent.toLocaleTimeString()}` : "…"}
              </div>
              <button onClick={stop} className="btn-secondary !py-1.5 text-xs" data-testid="driver-share-stop"><Square className="w-4 h-4" /> Stop</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
